import { Metamodel } from 'app/model/base/model';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { TransformationAdapter } from 'app/model/adapters/dataflow/transformations/base-transformation.adapter';
import { FLOW } from '../IDataflow';
import { DataFlowExpressionUtils } from '../expression.util';
import { ExpressionBasedColumnVisitorAdapterUtils } from '../expression-based-column-visitor-adapter.utils';
import { WindowTransformation } from 'app/model/dataflow/windowtransformation';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(WindowTransformation),
    asynchronous: true
})
export class WindowTransformationAdapter extends TransformationAdapter<WindowTransformation> {
    public async getDslScript(windowTransformation: WindowTransformation): Promise<string> {
        const props: string[] = [];
        if (windowTransformation.groupByColumns.length > 0) {
            props.push(ExpressionBasedColumnVisitorAdapterUtils.getGroupByColumnsDsl(windowTransformation, 'over'));
        }

        if (
            windowTransformation.range[0] !== WindowTransformation.UNBOUNDED ||
            windowTransformation.range[1] !== WindowTransformation.UNBOUNDED ||
            windowTransformation.sortExpressions.length > 1 ||
            (windowTransformation.sortExpressions.length === 1 &&
                windowTransformation.sortExpressions[0].dataFlowExpression.expression)
        ) {
            props.push(
                ...windowTransformation.sortExpressions.map((sortExpression) => {
                    // eslint-disable-next-line max-len
                    return `${
                        sortExpression.ascending ? 'asc' : 'desc'
                    }(${DataFlowExpressionUtils.wrapExpressionWithEmptyValue(
                        sortExpression.dataFlowExpression.expression
                    )}, ${sortExpression.nullsFirst ? 'true' : 'false'})`;
                })
            );
        }

        if(windowTransformation.rangeSpecified) {
            props.push(
                `rangeSpecified: true`
            )
        }

        if (windowTransformation.range[0] !== WindowTransformation.UNBOUNDED) {
            props.push(
                `${windowTransformation.rangeByOffset ? 'startRowOffset' : 'startRange'}: ${
                    windowTransformation.range[0]
                }`
            );
        }
        if (windowTransformation.range[1] !== WindowTransformation.UNBOUNDED) {
            props.push(
                `${windowTransformation.rangeByOffset ? 'endRowOffset' : 'endRange'}: ${windowTransformation.range[1]}`
            );
        }

        if (windowTransformation.transientExpressionColumns.length) {
            props.push(
                await ExpressionBasedColumnVisitorAdapterUtils.writeToDsl(
                    this.context.contextData,
                    windowTransformation.transientExpressionColumns,
                    windowTransformation
                )
            );
        }

        if (windowTransformation.expressionColumns.length) {
            props.push(
                await ExpressionBasedColumnVisitorAdapterUtils.writeToDsl(
                    this.context.contextData,
                    windowTransformation.expressionColumns,
                    windowTransformation
                )
            );
        }

        const partition = TransformationAdapter.getPartitionDslScript(windowTransformation);
        if (partition) {
            props.push(partition);
        }

        const knownProps = `${props.join(',\n\t')}`;
        const unknownProps = this._getUnknownProps(windowTransformation, knownProps);
        const transformationData = [knownProps, unknownProps].filter((c) => !!c).join(',\n\t');
        return `${this._getInputStreams(windowTransformation)} ${
            WindowTransformation.MODEL.name
        }(${transformationData}) ${FLOW} ${this._getOutputStreams(windowTransformation)}`;
    }
}
